import { Box, Theme, Typography } from "@outschool/backpack";
import {
  CategoryPage,
  SearchFilters,
  SearchUseCase,
} from "@outschool/gql-backend-generated";
import { SearchListingsQuery } from "@outschool/gql-frontend-generated";
import { TFunction, useTranslation } from "@outschool/localization";
import { useProductListViewed, useTrackEvent } from "@outschool/ui-analytics";
import { ErrorMessage, TrackedButton } from "@outschool/ui-components-shared";
import { useSearchQuery } from "@outschool/ui-components-website";
import {
  Container,
  Loading,
  Space,
} from "@outschool/ui-legacy-component-library";
import { responsive, useIsMobile } from "@outschool/ui-utils";
import { isEqual, omitBy } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import * as Env from "../../../../shared/Env";
import { ActivityListing } from "../../listings/ActivityListing";
import CardGrid from "../CardGrid";
import { ActivityGridLoadingState } from "../CardLoadingStates";

type Listings = SearchListingsQuery["searchListings"]["listings"];

export type CuratedCategoryPageActivityGridProps = {
  searchFilters: SearchFilters;
  loadedCategory: CategoryPage;
  loading: boolean;
  error?: Error;
  exactTotal: number;
  listings: Listings;
  searchUid: string | undefined;
  fetchMore?: () => void;
  isTutoringCLP: boolean;
};

export default function CuratedCategoryPageActivityGrid({
  searchFilters,
  loadedCategory,
  loading,
  error,
  exactTotal,
  listings,
  searchUid,
  fetchMore,
  isTutoringCLP,
}: CuratedCategoryPageActivityGridProps) {
  const trackProductListViewed = useProductListViewed();
  const isMobile = useIsMobile();

  useEffect(() => {
    const label = isMobile ? "mobile" : "desktop";
    if (!error && listings) {
      // @ts-ignore TS(2345): Argument of type '{ __typename?: "Listing" | undef... Remove this comment to see the full error message
      trackProductListViewed("curated-category-page", label, listings);
    }
  }, [isMobile, trackProductListViewed, listings, error]);

  const trackEvent = useTrackEvent();

  const { t } = useTranslation(
    "client\\components\\categories\\CuratedCategoryPageActivityGrid",
  );

  const defaultCardsPerPage = 12;
  const { fetchNext, paginatedListings, isPaginating } = usePaginatedResults({
    searchFilters,
    listings,
    fetchMore,
  });

  const ActivityListingsMemo = useMemo(() => {
    return (
      <ActivityListings
        listings={paginatedListings}
        loadedCategory={loadedCategory}
        searchUid={searchUid}
        isTutoringCLP={isTutoringCLP}
      />
    );
  }, [isTutoringCLP, loadedCategory, paginatedListings, searchUid]);

  if (error) {
    return (
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
    );
  }

  return (
    <>
      {!isTutoringCLP && <Space y={responsive({ default: 36, small: 34 })} />}
      {loading && (paginatedListings.length === 0 || !isPaginating) ? (
        <ActivityGridLoadingState
          includeHeading={false}
          cards={undefined}
          isTutoringCLP={isTutoringCLP}
        />
      ) : (
        <>
          {!exactTotal ? (
            <NoResults
              categoryName={loadedCategory.nameSentenceCased}
              // @ts-ignore TS(2322): Type 'Maybe<SavedSearchFilters>' is not assignable... Remove this comment to see the full error message
              categorySearchFilters={loadedCategory.searchFilters}
              loadedCategory={loadedCategory}
              t={t}
              isTutoringCLP={isTutoringCLP}
            />
          ) : (
            <>
              <Box data-test-id="category-listings">{ActivityListingsMemo}</Box>

              {exactTotal > listings.length && (
                <Box
                  sx={(theme: Theme) => ({
                    [theme.breakpoints.down("md")]: {
                      marginTop: "20px",
                    },
                  })}
                >
                  {paginatedListings.length > 0 && loading ? (
                    <Loading sx={{ marginY: 50 }} delay={0} />
                  ) : (
                    <ShowMoreButton
                      categorySlug={loadedCategory.slug}
                      onClick={() => {
                        fetchNext(defaultCardsPerPage);
                        trackEvent("curated_category_show_more_button", {
                          numberOfClassesShown: paginatedListings.length,
                          categorySlug: loadedCategory.slug,
                        });
                      }}
                      isSecondaryButton
                      t={t}
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

function usePaginatedResults({ searchFilters, fetchMore, listings }: $TSFixMe) {
  const [filters, setFilters] = useState<SearchFilters>({});
  const [isPaginating, setIsPaginating] = useState(false);
  const paginatedListings = listings;

  const fetchNext = useCallback(
    (cardsPerPage: number) => {
      const loadedAmount = listings.length;
      const currentAmount = paginatedListings.length;
      const expectedAmount = currentAmount + cardsPerPage;
      const hasToFetch = loadedAmount < expectedAmount;

      if (hasToFetch) {
        setIsPaginating(true);
        fetchMore();
      }
    },
    [listings, paginatedListings, fetchMore],
  );

  useEffect(() => {
    if (!isEqual(filters, searchFilters)) {
      setFilters(searchFilters);
      setIsPaginating(false);
    }
  }, [filters, searchFilters]);

  return {
    fetchNext,
    paginatedListings,
    isPaginating,
  };
}

const ShowMoreButton = ({
  categorySlug,
  onClick,
  isSecondaryButton = false,
  t,
}: $TSFixMe) => {
  return (
    <Container sx={{ display: "flex" }}>
      <TrackedButton
        variant={isSecondaryButton ? "outlined" : "contained"}
        trackingName="cclp_show_more"
        trackingUniqueId={categorySlug}
        sx={{
          margin: "auto",
          whiteSpace: "unset",
        }}
        onClick={onClick}
      >
        {t("Show More")}
      </TrackedButton>
    </Container>
  );
};

function NoResults({
  categoryName,
  categorySearchFilters,
  loadedCategory,
  t,
  isTutoringCLP,
}: {
  categoryName: string;
  categorySearchFilters: SearchFilters;
  loadedCategory: CategoryPage;
  t: TFunction;
  isTutoringCLP: boolean;
}) {
  const isMobile = useIsMobile();

  const headingText = t(
    "Oops! We couldn’t find an exact match for your filters.",
  );
  const subheadingText = t(
    "But here are some other {{ categoryName }} classes that you might like:",
    {
      categoryName: categoryName,
    },
  );

  const filters = omitBy(categorySearchFilters, v => v == null);
  delete filters["__typename"];

  const searchQueryResult = useSearchQuery(
    {
      referrer: document.referrer,
      variables: {
        limit: 8,
        offset: 0,
        pageLocation: "CURATED_CATEGORY_BACKUP_RESULTS",
        searchFilters: { ...filters, order: "popular" },
        skipRating: false,
        withRecentReviews: false,
        useCase: SearchUseCase.CategoryContent,
      },
    },
    true, // skipOtherListings
  );

  const {
    exactListings,
    loading,
    error,
    exactListingsSearchUid: searchUid,
  } = searchQueryResult;

  if (error) {
    return (
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
    );
  }

  if (loading) {
    <Loading sx={{ marginY: isMobile ? 100 : 250 }} delay={0} />;
  }

  return (
    <Container sx={{ textAlign: "center", paddingBottom: "40px" }}>
      <Typography variant="h6">{headingText}</Typography>
      {subheadingText}
      <Space y="medium" />
      <Box>
        <ActivityListings
          listings={exactListings}
          loadedCategory={loadedCategory}
          isTutoringCLP={isTutoringCLP}
          searchUid={searchUid}
        />
      </Box>
    </Container>
  );
}

function ActivityListings({
  listings,
  loadedCategory,
  searchUid,
  isTutoringCLP,
}: {
  listings: Listings;
  loadedCategory: CategoryPage;
  searchUid?: string;
  isTutoringCLP: boolean;
}) {
  const isMobile = useIsMobile();
  if (isMobile && !isTutoringCLP) {
    return (
      <Box sx={{ marginBottom: "1em" }}>
        {listings.map((listing, index: number) => (
          <ActivityListing
            key={listing.activity.uid}
            listing={listing}
            openInNewTab
            trackingProperties={{
              searchUid,
              order: index + 1,
              category_slug: loadedCategory.slug,
              category_display_strategy: "curated",
            }}
            listingIndex={index}
          />
        ))}
      </Box>
    );
  }

  return (
    <Container fullBleed={isMobile}>
      <CardGrid
        listings={listings}
        trackingLabel="cuarted_category_listing_desktop"
        trackingProperties={{
          searchUid,
          category_slug: loadedCategory.slug,
          category_display_strategy: "curated",
        }}
        rowCount={Math.floor(listings.length / 4)}
        isTutoringCLP={isTutoringCLP}
      />
    </Container>
  );
}
